import { useEffect, useState } from "react";

import { contentfulClient } from "../services/api.ts";
import { useGlobalContext } from "../utils/GlobalContext.ts";
import type { Documents } from "../graphql/graphql.ts";

const GetDocumentByPageUrl = `
  query GetDocumentByPageUrl(
    $pageUrl: String!
    $locale: String
    $preview: Boolean
  ) {
    documentsCollection(
      locale: $locale
      preview: $preview
      where: { documentPageUrl: $pageUrl }
      limit: 1
    ) {
      items {
        sys {
          id
        }
        documentPageUrl
        documentTitle
        file {
          url
          title
          contentType
          fileName
        }
      }
    }
  }
`;

interface GetDocumentByPageUrlResponse {
  documentsCollection?: {
    items: Array<Documents | null>;
  } | null;
}

interface UseDocumentByPageUrlResult {
  document: Documents | null;
  loading: boolean;
  notFound: boolean;
  error: boolean;
}

export function useDocumentByPageUrl(
  pageUrl: string,
): UseDocumentByPageUrlResult {
  const { localeConfig, previewContent } = useGlobalContext();
  const [document, setDocument] = useState<Documents | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function fetchDocument() {
      try {
        setLoading(true);
        setError(false);
        setNotFound(false);

        const response =
          await contentfulClient.query<GetDocumentByPageUrlResponse>(
            GetDocumentByPageUrl,
            {
              pageUrl,
              locale: localeConfig.currentLocale ?? undefined,
              preview: Boolean(previewContent),
            },
          );

        if (cancelled) return;

        const item = response.documentsCollection?.items?.[0];

        if (!item) {
          setNotFound(true);
          setDocument(null);
          return;
        }

        setDocument(item);
      } catch (err) {
        if (cancelled) return;
        console.error("Error fetching Contentful document:", err);
        setError(true);
        setDocument(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void fetchDocument();

    return () => {
      cancelled = true;
    };
  }, [pageUrl, localeConfig.currentLocale, previewContent]);

  return { document, loading, notFound, error };
}
